import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AddIcon from '@mui/icons-material/Add';

const AdminDramaGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [generatedEpisodes, setGeneratedEpisodes] = useState([]);
  const [progress, setProgress] = useState(0);
  
  // 表单数据
  const [formData, setFormData] = useState({
    dramaName: '',
    dramaDescription: '',
    dramaCategory: '悬疑',
    maxDuration: 60,
  });
  
  // 视频文件路径列表（手动填写）
  const [videoPaths, setVideoPaths] = useState(['']);

  // 处理表单输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理视频路径输入变化
  const handlePathChange = (index, value) => {
    const newPaths = [...videoPaths];
    newPaths[index] = value;
    setVideoPaths(newPaths);
  };

  // 添加新的视频路径输入框
  const handleAddPath = () => {
    setVideoPaths(prev => [...prev, '']);
  };

  // 删除视频路径
  const handleRemovePath = (index) => {
    if (videoPaths.length > 1) {
      setVideoPaths(prev => prev.filter((_, i) => i !== index));
    }
  };

  // 生成剧集的核心逻辑
  const handleGenerateEpisodes = async () => {
    // 过滤空路径
    const validPaths = videoPaths.filter(p => p.trim());
    
    if (validPaths.length === 0) {
      setError('请至少填写一个视频文件路径');
      return;
    }
    
    if (!formData.dramaName.trim()) {
      setError('请输入剧集名称');
      return;
    }
    
    if (!formData.maxDuration || formData.maxDuration < 10) {
      setError('请设置有效的视频最长长度（至少10秒）');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedEpisodes([]);
    setProgress(0);

    try {
      const requestData = {
        dramaName: formData.dramaName,
        dramaDescription: formData.dramaDescription,
        dramaCategory: formData.dramaCategory,
        maxDuration: formData.maxDuration,
        videoPaths: validPaths,
      };

      // 模拟进度更新
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 500);

      const response = await fetch('/api/drama-generator/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      clearInterval(progressInterval);
      setProgress(100);

      const result = await response.json();

      if (result.success) {
        setSuccess(`成功生成 ${result.data.episodes.length} 集剧集！`);
        setGeneratedEpisodes(result.data.episodes);
      } else {
        setError(result.error || '生成失败');
      }
    } catch (err) {
      console.error('生成失败:', err);
      setError('网络请求失败，请检查服务器连接');
    } finally {
      setLoading(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    setFormData({
      dramaName: '',
      dramaDescription: '',
      dramaCategory: '悬疑',
      maxDuration: 60,
    });
    setVideoPaths(['']);
    setGeneratedEpisodes([]);
    setProgress(0);
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom align="center">
          剧集自动生成部署
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          填写视频文件路径，设置单集时长，自动分割并部署为完整剧集
        </Typography>

        {/* 第一行：原始文件位置 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            1. 原始文件位置（按顺序处理）
          </Typography>
          
          <List dense sx={{ bgcolor: '#fafafa', borderRadius: 1, p: 1 }}>
            {videoPaths.map((path, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  videoPaths.length > 1 && (
                    <IconButton edge="end" size="small" onClick={() => handleRemovePath(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <VideoFileIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ mr: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`视频文件路径 ${index + 1} (如: /data/video/part${index + 1}.mp4)`}
                    value={path}
                    onChange={(e) => handlePathChange(index, e.target.value)}
                  />
                </ListItemText>
              </ListItem>
            ))}
          </List>
          
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddPath}
            size="small"
            sx={{ mt: 1 }}
          >
            添加视频文件
          </Button>
        </Box>

        {/* 第二行：视频最长长度 + 剧集名称 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            2. 剧集基本信息
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="剧集名称"
                name="dramaName"
                value={formData.dramaName}
                onChange={handleInputChange}
                placeholder="请输入剧集名称"
                required
                helperText="生成后将作为剧集的正式名称"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="视频最长长度（秒）"
                name="maxDuration"
                type="number"
                value={formData.maxDuration}
                onChange={handleInputChange}
                InputProps={{
                  inputProps: { min: 10, max: 600 }
                }}
                helperText="分割后单集的最大时长，建议30-120秒"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>剧集类型</InputLabel>
                <Select
                  name="dramaCategory"
                  value={formData.dramaCategory}
                  label="剧集类型"
                  onChange={handleInputChange}
                >
                  <MenuItem value="悬疑">悬疑</MenuItem>
                  <MenuItem value="爱情">爱情</MenuItem>
                  <MenuItem value="动作">动作</MenuItem>
                  <MenuItem value="科幻">科幻</MenuItem>
                  <MenuItem value="喜剧">喜剧</MenuItem>
                  <MenuItem value="恐怖">恐怖</MenuItem>
                  <MenuItem value="都市">都市</MenuItem>
                  <MenuItem value="古装">古装</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="剧集描述"
                name="dramaDescription"
                value={formData.dramaDescription}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="请简要描述这部剧集的内容"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 操作按钮 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleGenerateEpisodes}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              size="large"
              color="primary"
            >
              {loading ? '正在生成...' : '开始生成剧集'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              startIcon={<DeleteIcon />}
              size="large"
            >
              重置
            </Button>
          </Box>

          {/* 进度条 */}
          {loading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                处理进度: {progress}%
              </Typography>
              <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 2, height: 8 }}>
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: '100%',
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                    transition: 'width 0.3s ease'
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* 生成结果展示 */}
        {generatedEpisodes.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              生成结果
            </Typography>
            
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`剧集名称: ${formData.dramaName}`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`共 ${generatedEpisodes.length} 集`} 
                color="success" 
              />
              <Chip 
                label={`类型: ${formData.dramaCategory}`} 
                variant="outlined" 
              />
              <Chip 
                label={`单集最长: ${formData.maxDuration}秒`} 
                variant="outlined" 
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell width="80">集数</TableCell>
                    <TableCell>单集标题</TableCell>
                    <TableCell width="100">时长（秒）</TableCell>
                    <TableCell width="100">文件大小</TableCell>
                    <TableCell>视频路径</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generatedEpisodes.map((episode, index) => (
                    <TableRow key={episode.id || index} hover>
                      <TableCell>
                        <Chip label={`第 ${index + 1} 集`} size="small" color="primary" />
                      </TableCell>
                      <TableCell>{episode.title || `${formData.dramaName} 第${index + 1}集`}</TableCell>
                      <TableCell>{episode.duration || formData.maxDuration}s</TableCell>
                      <TableCell>{episode.fileSize || '--'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {episode.videoPath || episode.video_url || '--'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* 消息提示 */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDramaGenerator;
